// src/pages/AxiomPortfolioV3.jsx
// AXIOM Intelligence Platform — Light Institutional Skin
// Aesthetic: McKinsey Exhibit × PwC Annual Report × Blackrock Deck
// Warm white · Deep charcoal · Single gold accent · Editorial data density

import React, { useState, useCallback } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — Light institutional palette
// ════════════════════════════════════════════════════════════════════════════
const T = {
  bg:            '#F5F3EF',
  bgCard:        '#FFFFFF',
  bgElevated:    '#FAFAF8',
  bgHover:       '#F0EDE8',
  bgDark:        '#1A1A1A',
  border:        '#E2DDD6',
  borderStrong:  '#C8C2B8',
  textPrimary:   '#1A1814',
  textSecondary: '#5C5850',
  textMuted:     '#9C9890',
  textInverse:   '#F5F3EF',
  gold:          '#9A7B2E',
  goldLight:     '#C4992A',
  goldBg:        'rgba(154,123,46,0.06)',
  goldBorder:    'rgba(154,123,46,0.18)',
  red:           '#8B1A1A',
  redLight:      '#B32020',
  redBg:         'rgba(139,26,26,0.05)',
  redBorder:     'rgba(139,26,26,0.15)',
  green:         '#1A4D2E',
  greenLight:    '#1E6B3A',
  greenBg:       'rgba(26,77,46,0.05)',
  greenBorder:   'rgba(26,77,46,0.15)',
  amber:         '#7A5C0A',
  amberLight:    '#9A7210',
  amberBg:       'rgba(122,92,10,0.05)',
  amberBorder:   'rgba(122,92,10,0.15)',
  navy:          '#1A2D5A',
  navyBg:        'rgba(26,45,90,0.05)',
  navyBorder:    'rgba(26,45,90,0.15)',
};

const PHASES = ['Site', 'Design', 'Build', 'Commission', 'Ops', 'Exit'];

const phaseColor = (phase) => ({
  Site:       '#2E6EA6',
  Design:     '#5B4E8A',
  Build:      '#9A7B2E',
  Commission: '#8B1A1A',
  Ops:        '#1A4D2E',
  Exit:       '#1A2D5A',
}[phase] || '#9C9890');

const scoreStyle = (score, phase) => {
  if (!score) return { color: T.textMuted, label: '—' };
  if (phase === 'Exit' && score >= 80) return { color: T.navy, label: 'Exit Ready' };
  if (score >= 80) return { color: T.greenLight, label: 'Low Risk' };
  if (score >= 55) return { color: T.amberLight, label: 'Monitor' };
  if (score >= 35) return { color: '#B05010', label: 'Elevated' };
  return { color: T.redLight, label: 'Critical' };
};

// ════════════════════════════════════════════════════════════════════════════
// POLICY REGISTRY
// ════════════════════════════════════════════════════════════════════════════
const POLICIES = {
  'COMM-001': { id: 'COMM-001', name: 'IST Sequence Conflict',       category: 'Commission' },
  'COMM-002': { id: 'COMM-002', name: 'P1 Punch List · SLA Window',  category: 'Commission' },
  'COMM-003': { id: 'COMM-003', name: 'Surge Crew Authorization',    category: 'Commission' },
  'BILD-001': { id: 'BILD-001', name: 'Vendor Lead Time Deviation',  category: 'Build' },
  'BILD-002': { id: 'BILD-002', name: 'Portfolio Equipment Pattern', category: 'Build' },
  'BILD-003': { id: 'BILD-003', name: 'RFI Escalation Threshold',   category: 'Build' },
  'SITE-001': { id: 'SITE-001', name: 'Grid Queue Threshold',        category: 'Site' },
  'SITE-002': { id: 'SITE-002', name: 'Latency Spec Validation',    category: 'Site' },
  'SITE-003': { id: 'SITE-003', name: 'Water Rights Check',          category: 'Site' },
  'CPTL-001': { id: 'CPTL-001', name: 'Exit Window Optimization',   category: 'Capital' },
  'CPTL-002': { id: 'CPTL-002', name: 'PUE Optimization',           category: 'Capital' },
  'CPTL-003': { id: 'CPTL-003', name: 'Budget Variance',            category: 'Capital' },
};

const PROJECT_POLICIES = {
  'dal-01': { assigned: ['COMM-001','COMM-002','COMM-003','BILD-001','CPTL-003'], triggered: ['COMM-001','COMM-002'] },
  'phx-02': { assigned: ['SITE-001','SITE-003','BILD-003','CPTL-003'],            triggered: ['SITE-001'] },
  'iad-03': { assigned: ['CPTL-001','CPTL-002'],                                  triggered: [] },
  'atl-04': { assigned: ['BILD-001','BILD-002','BILD-003','COMM-001'],            triggered: ['BILD-001','BILD-002'] },
  'den-05': { assigned: ['SITE-002','SITE-003'],                                  triggered: [] },
  'chi-06': { assigned: ['SITE-002','BILD-003','CPTL-003'],                       triggered: ['SITE-002'] },
};

// ════════════════════════════════════════════════════════════════════════════
// CORPUS DATA
// ════════════════════════════════════════════════════════════════════════════
const CORPUS = {
  'dal-01': {
    withoutAxiom: { outcome: '$46M schedule slip',          detail: 'IST conflict undetected until go-live week. 23-day delay, SLA breach, penalty clauses triggered.' },
    withAxiom:    { outcome: '$4M surge · 19 days recovered', detail: 'Conflict flagged 23 days early. Parallel IST tracks authorized via COMM-001.' },
    savedValue: '$42M', confidence: 94,
    corpusMatches: [
      { project: 'eBay Salt Lake City', year: 2018, outcome: 'Parallel IST resolved 21-day slip in 8 days', relevance: 98 },
      { project: 'Switch LAS 4',        year: 2020, outcome: 'CRAC sequencing — 14-day slip, $28M penalty', relevance: 91 },
      { project: 'Aligned PHX 2',       year: 2022, outcome: 'Surge protocol — 11 days recovered at $2.4M', relevance: 87 },
    ],
    reasoning: [
      { step: 'Pattern detection',  detail: 'IST schedule ingested. CRAC window overlaps electrical testing 11 days — COMM-001 triggered.' },
      { step: 'Corpus search',      detail: '47 commissioning sequences searched across $5B project history. 3 high-confidence matches.' },
      { step: 'Resolution model',   detail: 'eBay SLC 2018 at 98% similarity. Parallel IST + 48hr surge crew: 19/21 days recovered.' },
      { step: 'Counterfactual',     detail: 'Without action: May 1 breach certain. $2M/day × 23 days = $46M exposure.' },
    ],
    recommendation: 'Authorize parallel IST tracks immediately. Mobilize surge crew by April 8. Cost ~$4M. Net protection: $42M.',
    triggeredPolicies: ['COMM-001','COMM-002'],
  },
  'phx-02': {
    withoutAxiom: { outcome: '6-month grid delay',     detail: 'APS queue not flagged. BOD locked at 18-month assumption. Delayed Q1 2027.' },
    withAxiom:    { outcome: '~4 months recovered',    detail: 'Queue flagged immediately. Pre-application advanced. Alternative substation identified 11 miles east.' },
    savedValue: '$18M', confidence: 88,
    corpusMatches: [
      { project: 'Switch PHX 1',    year: 2019, outcome: 'Same APS substation — 22-month queue, relocated', relevance: 95 },
      { project: 'QTS Chandler',    year: 2021, outcome: 'Pre-application advanced 5 months', relevance: 89 },
      { project: 'Aligned Gilbert', year: 2023, outcome: 'Alternative feed saved 6 months', relevance: 83 },
    ],
    reasoning: [
      { step: 'Site scoring',          detail: 'APS Estrella: 18 months. Corpus median: 12. SITE-001 at +6mo variance.' },
      { step: 'Alternative analysis',  detail: 'SRP Superstition: 6-month queue, 11 miles east. Delta: +$800K.' },
      { step: 'Pre-application model', detail: 'Filing now recovers ~4 months. Expected: 14 months.' },
      { step: 'BOD impact',            detail: 'Hybrid liquid: +6wk commission, −22% 5yr OpEx. NPV positive.' },
    ],
    recommendation: 'File APS pre-application this week. Resolve BOD cooling by April 10.',
    triggeredPolicies: ['SITE-001'],
  },
  'iad-03': {
    withoutAxiom: { outcome: 'Off-cycle exit, −18% multiple', detail: 'Q4 2025 misses peak. $380K PUE opportunity unrealized.' },
    withAxiom:    { outcome: 'Q2 2026 peak window',           detail: 'Demand cycle modeled. PUE adds $2.8M. 4 buyers with active mandates.' },
    savedValue: '$180M+ uplift', confidence: 91,
    corpusMatches: [
      { project: 'Switch REIT IPO',     year: 2017, outcome: '11× EBITDA — peak demand cycle', relevance: 96 },
      { project: 'QTS Realty',          year: 2021, outcome: 'Blackrock 28× EBITDA', relevance: 92 },
      { project: 'Aligned (Blackrock)', year: 2025, outcome: '$40B — sovereign AI premium', relevance: 89 },
    ],
    reasoning: [
      { step: 'Demand cycle', detail: 'Q2 2026 = peak AI infrastructure window per 12-transaction model.' },
      { step: 'Buyers',       detail: '4 active mandates: Blackrock, Macquarie, Brookfield, GIC.' },
      { step: 'PUE',          detail: '$380K/yr savings. 5yr NPV at exit: $2.8M.' },
      { step: 'Governance',   detail: '5yr clean audit. GRESB Tier 1. S-1 ready.' },
    ],
    recommendation: 'Execute PUE optimization now. Target sale Q1 2026, close Q2 2026.',
    triggeredPolicies: [],
  },
  'atl-04': {
    withoutAxiom: { outcome: 'Vertiv cascade — 3 sites',      detail: 'Delay undetected. Commissioning pushed Q1 2026. SLA penalties triggered.' },
    withAxiom:    { outcome: '$28M protected — Eaton auth.',   detail: 'Cross-portfolio pattern detected. Single procurement action covers all 3 sites.' },
    savedValue: '$28M (3 sites)', confidence: 87,
    corpusMatches: [
      { project: 'Switch LAS 2022', year: 2022, outcome: '14-week slip, $22M penalty', relevance: 97 },
      { project: 'eBay PHX 2019',   year: 2019, outcome: 'Eaton substitution — zero impact', relevance: 94 },
      { project: 'CyrusOne ATL',    year: 2021, outcome: 'Dual-source standard implemented', relevance: 81 },
    ],
    reasoning: [
      { step: 'Portfolio detection', detail: 'BILD-001: Vertiv +11wk across Dallas, Atlanta, Seattle.' },
      { step: 'Pattern match',       detail: 'BILD-002: Switch LAS 2022 — identical SKU, $22M penalty.' },
      { step: 'Mitigation',          detail: 'Eaton compatible. Delta: +$340K. Zero schedule impact.' },
      { step: 'Portfolio action',    detail: 'One decision protects $28M across 3 sites.' },
    ],
    recommendation: 'Authorize Eaton across Dallas, Atlanta, Seattle. Cost ~$1M. Protection: $28M.',
    triggeredPolicies: ['BILD-001','BILD-002'],
  },
  'den-05': {
    withoutAxiom: { outcome: '$8M site switch at construction', detail: 'Fiber route not analyzed. Latency conflict discovered at steel.' },
    withAxiom:    { outcome: 'Clean site — advance to BOD',    detail: 'SITE-002: 1.8ms to CME confirmed. SITE-003: water rights secured.' },
    savedValue: '$8M avoided', confidence: 82,
    corpusMatches: [
      { project: 'Iron Mountain DEN', year: 2020, outcome: '$6M sunk cost — site abandoned', relevance: 88 },
      { project: 'Aligned DEN 1',    year: 2022, outcome: 'Water rights upfront — no renegotiation', relevance: 84 },
    ],
    reasoning: [
      { step: 'Latency', detail: 'SITE-002: 1.8ms to CME via Zayo — within spec.' },
      { step: 'Water',   detail: 'SITE-003: Denver Basin 1.2M gal/day confirmed.' },
      { step: 'Score',   detail: '88th percentile vs corpus.' },
    ],
    recommendation: 'Advance to BOD. Secure water rights before competing applications.',
    triggeredPolicies: [],
  },
  'chi-06': {
    withoutAxiom: { outcome: '$12M rework at commissioning',  detail: 'Fiber conflict after steel. Structural remediation.' },
    withAxiom:    { outcome: '$2.1M dark fiber at BOD',       detail: 'SITE-002: design addendum before construction. Zero structural impact.' },
    savedValue: '$12M avoided', confidence: 79,
    corpusMatches: [
      { project: 'Equinix CH6',  year: 2019, outcome: 'Dark fiber $2M vs $14M rework', relevance: 91 },
      { project: 'QTS Chicago',  year: 2022, outcome: 'Resolved at BOD — zero impact', relevance: 86 },
    ],
    reasoning: [
      { step: 'Latency', detail: 'SITE-002: 0.8ms vs <0.5ms. Out of spec.' },
      { step: 'Options', detail: 'Dark fiber $2.1M. Before steel: addendum only.' },
      { step: 'Timing',  detail: 'After steel: 8× cost.' },
    ],
    recommendation: 'Procure dark fiber this week. $2.1M protects $12M.',
    triggeredPolicies: ['SITE-002'],
  },
};

// ════════════════════════════════════════════════════════════════════════════
// PROJECT DATA
// ════════════════════════════════════════════════════════════════════════════
const PROJECTS = [
  {
    id: 'dal-01', name: 'Lone Star AI Campus',         location: 'Dallas, TX',    type: '400MW AI Factory',
    phase: 'Commission', phaseScore: 34, capital: '$1.1B', capitalPct: 82, tenant: 'Microsoft',
    signal: { sev: 'critical', text: '23-day IST slip · $2M/day exposure', policy: 'COMM-001' },
    metrics: [{ label: 'Days to SLA', value: '41', warn: true }, { label: 'P1 Punch', value: '41 open', warn: true }, { label: 'IST Status', value: '61%', warn: true }],
    allMetrics: [
      { label: 'Days to SLA', value: '41', warn: true }, { label: 'Open P1', value: '41', warn: true },
      { label: 'Cost of Slip', value: '$2M/day', warn: true }, { label: 'IST Complete', value: '61%', warn: true },
      { label: 'Hyperscaler SLA', value: 'May 1', warn: true }, { label: 'Budget Variance', value: '+2.1%', warn: false },
    ],
    phases: [
      { name: 'Site', score: 91, done: true, note: 'Grid secured Q1 2023' },
      { name: 'Design', score: 84, done: true, note: 'Hybrid liquid — BOD locked' },
      { name: 'Build', score: 76, done: true, note: 'Steel complete, MEP 94%' },
      { name: 'Commission', score: 34, done: false, note: 'IST conflict active' },
      { name: 'Ops', score: null, done: false, note: 'Target Jun 2025' },
      { name: 'Exit', score: null, done: false, note: 'Strategic, 2028' },
    ],
    insight: 'COMM-001: eBay SLC 2018 at 98% match — identical CRAC/electrical conflict. Parallel IST recovers 19 of 23 days.',
  },
  {
    id: 'phx-02', name: 'Sonoran Compute Park',        location: 'Phoenix, AZ',   type: '250MW Co-location',
    phase: 'Design', phaseScore: 71, capital: '$44M', capitalPct: 16, tenant: 'Pre-leased 60%',
    signal: { sev: 'warn', text: 'APS grid queue +6mo above corpus median', policy: 'SITE-001' },
    metrics: [{ label: 'BOD Lock', value: 'Apr 14', warn: true }, { label: 'Grid Queue', value: '18 months', warn: true }, { label: 'Pre-leased', value: '60%', warn: false }],
    allMetrics: [
      { label: 'BOD Lock', value: 'Apr 14', warn: true }, { label: 'Open Decisions', value: '3', warn: true },
      { label: 'Grid Queue', value: '18 months', warn: true }, { label: 'Pre-leased', value: '60%', warn: false },
      { label: 'Budget Locked', value: '78%', warn: false }, { label: 'Corpus Match', value: 'Switch PHX', warn: false },
    ],
    phases: [
      { name: 'Site', score: 88, done: true, note: 'Maricopa — permit in hand' },
      { name: 'Design', score: 71, done: false, note: 'BOD lock Apr 14' },
      { name: 'Build', score: null, done: false, note: 'GC: McCarthy' },
      { name: 'Commission', score: null, done: false, note: 'Target Q3 2026' },
      { name: 'Ops', score: null, done: false, note: '—' },
      { name: 'Exit', score: null, done: false, note: 'IPO 2029' },
    ],
    insight: 'SITE-001: APS Estrella 18mo vs 12mo corpus median. Pre-application now recovers ~4 months.',
  },
  {
    id: 'iad-03', name: 'Virginia Hyperscale Cluster',  location: 'Ashburn, VA',  type: '600MW Campus · 3 bldgs',
    phase: 'Exit', phaseScore: 94, capital: '$2.1B', capitalPct: 100, tenant: 'Amazon, Google',
    signal: { sev: 'ok', text: 'Exit readiness 94% · Q2 2026 window optimal', policy: 'CPTL-001' },
    metrics: [{ label: 'Implied Val.', value: '$2.8B', warn: false }, { label: 'Buyers', value: '4 active', warn: false }, { label: 'PUE', value: '1.21', warn: false }],
    allMetrics: [
      { label: 'Uptime', value: '99.991%', warn: false }, { label: 'PUE Actual', value: '1.21', warn: false },
      { label: 'Tenancy', value: '94%', warn: false }, { label: 'NOI Growth', value: '+8.4%', warn: false },
      { label: 'Implied Val.', value: '$2.8B', warn: false }, { label: 'Strategic Buyers', value: '4', warn: false },
    ],
    phases: [
      { name: 'Site', score: 95, done: true, note: 'Dominion direct feed' },
      { name: 'Design', score: 91, done: true, note: 'PUE 1.18 target' },
      { name: 'Build', score: 88, done: true, note: '+4 days ahead' },
      { name: 'Commission', score: 82, done: true, note: 'Q2 2023 — no slips' },
      { name: 'Ops', score: 82, done: true, note: 'PUE 1.21 actual' },
      { name: 'Exit', score: 94, done: false, note: 'Q2 2026 — optimal' },
    ],
    insight: 'CPTL-001: Q2 2026 peak window confirmed. CPTL-002: $380K/yr PUE adds $2.8M to exit multiple.',
  },
  {
    id: 'atl-04', name: 'Peachtree Edge Data',          location: 'Atlanta, GA',   type: '180MW Modular',
    phase: 'Build', phaseScore: 58, capital: '$340M', capitalPct: 51, tenant: 'Meta (LOI)',
    signal: { sev: 'warn', text: 'Vertiv UPS +11wk · pattern across 3 sites', policy: 'BILD-002' },
    metrics: [{ label: 'Steel', value: '70%', warn: false }, { label: 'Open RFIs', value: '31 (8 P1)', warn: true }, { label: 'Vertiv Delay', value: '+11 weeks', warn: true }],
    allMetrics: [
      { label: 'Steel Progress', value: '70%', warn: false }, { label: 'MEP Progress', value: '45%', warn: false },
      { label: 'Open RFIs', value: '31 (8 P1)', warn: true }, { label: 'Vertiv Delay', value: '+11 weeks', warn: true },
      { label: 'Budget Variance', value: '+1.2%', warn: false }, { label: 'Schedule', value: '2 days ahead', warn: false },
    ],
    phases: [
      { name: 'Site', score: 93, done: true, note: 'GA Power 48hr' },
      { name: 'Design', score: 87, done: true, note: 'Modular liquid cooling' },
      { name: 'Build', score: 58, done: false, note: 'Steel 70%, MEP 45%' },
      { name: 'Commission', score: null, done: false, note: 'Target Nov 2025' },
      { name: 'Ops', score: null, done: false, note: '—' },
      { name: 'Exit', score: null, done: false, note: 'Strategic 2028' },
    ],
    insight: 'BILD-001 + BILD-002: Vertiv pattern across 3 sites. Eaton substitution confirmed compatible. $28M exposure.',
  },
  {
    id: 'den-05', name: 'Mile High AI Hub',              location: 'Denver, CO',    type: '120MW Hyperscale',
    phase: 'Site', phaseScore: 88, capital: '$3M', capitalPct: 1, tenant: 'Financial Exchange',
    signal: { sev: 'ok', text: 'Site score 88th pct · water rights secured', policy: 'SITE-003' },
    metrics: [{ label: 'Site Score', value: '88/100', warn: false }, { label: 'Latency CME', value: '1.8ms', warn: false }, { label: 'Tax Incentive', value: '$12M', warn: false }],
    allMetrics: [
      { label: 'Site Score', value: '88/100', warn: false }, { label: 'Water Rights', value: 'Secured', warn: false },
      { label: 'Latency CME', value: '1.8ms', warn: false }, { label: 'Tax Incentive', value: '$12M', warn: false },
      { label: 'Corpus Comps', value: '11 sites', warn: false }, { label: 'Power Access', value: '6 months', warn: false },
    ],
    phases: [
      { name: 'Site', score: 88, done: false, note: 'Water rights confirmed' },
      { name: 'Design', score: null, done: false, note: 'GC selection' },
      { name: 'Build', score: null, done: false, note: 'Q3 2026' },
      { name: 'Commission', score: null, done: false, note: '—' },
      { name: 'Ops', score: null, done: false, note: '—' },
      { name: 'Exit', score: null, done: false, note: '—' },
    ],
    insight: 'SITE-002: 1.8ms to CME confirmed. SITE-003: Denver Basin water rights secured. Advance to BOD.',
  },
  {
    id: 'chi-06', name: 'Midwest Sovereign Exchange',   location: 'Chicago, IL',   type: '200MW Financial Edge',
    phase: 'Design', phaseScore: 67, capital: '$28M', capitalPct: 6, tenant: 'Financial / Gov',
    signal: { sev: 'warn', text: 'Latency conflict · 0.8ms vs <0.5ms spec', policy: 'SITE-002' },
    metrics: [{ label: 'Fiber Latency', value: '0.8ms', warn: true }, { label: 'Tenant Spec', value: '<0.5ms', warn: true }, { label: 'Remediation', value: '$2.1M', warn: false }],
    allMetrics: [
      { label: 'BOD Status', value: 'In Review', warn: true }, { label: 'Fiber Latency', value: '0.8ms', warn: true },
      { label: 'Tenant Spec', value: '<0.5ms', warn: true }, { label: 'Remediation', value: '$2.1M', warn: false },
      { label: 'Site Score', value: '91/100', warn: false }, { label: 'Power Access', value: 'Secured', warn: false },
    ],
    phases: [
      { name: 'Site', score: 91, done: true, note: 'ComEd direct feed' },
      { name: 'Design', score: 67, done: false, note: 'Fiber route review' },
      { name: 'Build', score: null, done: false, note: 'Q2 2026' },
      { name: 'Commission', score: null, done: false, note: '—' },
      { name: 'Ops', score: null, done: false, note: '—' },
      { name: 'Exit', score: null, done: false, note: 'IPO 2030' },
    ],
    insight: 'SITE-002: 0.8ms vs <0.5ms. Equinix CH6 2019 match — dark fiber $2.1M vs $12M rework.',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// POLICY PILL — light version
// ════════════════════════════════════════════════════════════════════════════
const PolicyPill = ({ id, fired }) => {
  if (!POLICIES[id]) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 6px', borderRadius: 3,
      fontSize: 9, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.05em',
      background: fired ? T.goldBg : 'rgba(26,24,20,0.04)',
      border: `1px solid ${fired ? T.goldBorder : T.border}`,
      color: fired ? T.gold : T.textMuted,
    }}>
      {fired && <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.goldLight, display: 'inline-block' }} />}
      {id}
    </span>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CORPUS MODAL — light
// ════════════════════════════════════════════════════════════════════════════
const CorpusModal = ({ projectId, onClose }) => {
  const project = PROJECTS.find(p => p.id === projectId);
  const corpus = CORPUS[projectId];
  if (!project || !corpus) return null;
  const pp = PROJECT_POLICIES[projectId] || { assigned: [], triggered: [] };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,24,20,0.5)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto',
        background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10,
        boxShadow: '0 24px 60px rgba(26,24,20,0.18)',
        animation: 'fadeScaleIn 0.2s ease-out',
      }} onClick={e => e.stopPropagation()}>

        {/* Gold top rule */}
        <div style={{ height: 3, background: T.goldLight, borderRadius: '10px 10px 0 0' }} />

        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.2em', color: T.gold, textTransform: 'uppercase', marginBottom: 4 }}>AXIOM · Corpus Intelligence</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{project.name}</div>
              <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
                {pp.triggered.map(id => <PolicyPill key={id} id={id} fired={true} />)}
                {pp.assigned.filter(id => !pp.triggered.includes(id)).map(id => <PolicyPill key={id} id={id} fired={false} />)}
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`,
              color: T.textSecondary, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {[
              { label: 'Corpus Size', value: '$5B',                         sub: 'Real project history' },
              { label: 'Matches',     value: corpus.corpusMatches.length,    sub: 'High-confidence comps' },
              { label: 'Confidence',  value: `${corpus.confidence}%`,        sub: 'Pattern match score',
                color: corpus.confidence >= 90 ? T.greenLight : T.amberLight },
            ].map((s, i) => (
              <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: s.color || T.textPrimary, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 9, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 5 }}>{s.label}</div>
                <div style={{ fontSize: 9, color: T.textMuted, marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Counterfactual */}
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Counterfactual Analysis</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: T.redLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Without Axiom</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.red, fontFamily: 'Georgia, serif', marginBottom: 6 }}>{corpus.withoutAxiom.outcome}</div>
                <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>{corpus.withoutAxiom.detail}</div>
              </div>
              <div style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 8, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: T.greenLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>With Axiom</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.green, fontFamily: 'Georgia, serif', marginBottom: 6 }}>{corpus.withAxiom.outcome}</div>
                <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>{corpus.withAxiom.detail}</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <span style={{ fontSize: 11, color: T.textMuted }}>Protected value: </span>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.greenLight, fontFamily: 'Georgia, serif' }}>{corpus.savedValue}</span>
            </div>
          </div>

          {/* Corpus matches */}
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Corpus Matches — $5B Project History</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {corpus.corpusMatches.map((m, i) => (
                <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{m.project}</span>
                      <span style={{ fontSize: 10, color: T.textMuted }}>{m.year}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 60, height: 3, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: T.goldLight, width: `${m.relevance}%`, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.gold }}>{m.relevance}%</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{m.outcome}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reasoning */}
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Reasoning Chain</div>
            {corpus.reasoning.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: T.goldBg, border: `1px solid ${T.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: T.gold }}>{i + 1}</div>
                  {i < corpus.reasoning.length - 1 && <div style={{ width: 1, flex: 1, background: T.border, margin: '4px 0' }} />}
                </div>
                <div style={{ paddingBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textPrimary, marginBottom: 2 }}>{r.step}</div>
                  <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>{r.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Recommendation */}
          <div style={{ background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 8 }}>Axiom Recommendation</div>
            <p style={{ fontSize: 12, color: T.textPrimary, lineHeight: 1.6, margin: 0 }}>{corpus.recommendation}</p>
          </div>

          <div style={{ textAlign: 'center', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', paddingBottom: 4 }}>
            Powered by $5B project corpus · 15+ hyperscale builds · Cradle to exit
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// HERO CARD — light
// ════════════════════════════════════════════════════════════════════════════
const HeroCard = ({ onOpenCorpus }) => {
  const corpus = CORPUS['dal-01'];
  return (
    <div style={{ marginBottom: 20, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.redBorder}`, background: T.bgCard, boxShadow: '0 2px 12px rgba(139,26,26,0.06)' }}>
      <div style={{ height: 3, background: T.redLight }} />
      <div style={{ padding: '20px 24px' }}>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.redLight, animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 9, fontWeight: 800, color: T.redLight, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Active Intervention · Axiom Build</span>
              <PolicyPill id="COMM-001" fired={true} />
              <PolicyPill id="COMM-002" fired={true} />
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary, fontFamily: 'Georgia, serif', letterSpacing: '-0.01em' }}>Lone Star AI Campus — Commission Phase</div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>Dallas, TX · 400MW AI Factory · Microsoft SLA May 1, 2026</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 38, fontWeight: 800, color: T.red, fontFamily: 'Georgia, serif', lineHeight: 1 }}>$42M</div>
            <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', marginTop: 3 }}>value protected</div>
          </div>
        </div>

        {/* Counterfactual */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div style={{ background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.redLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Without Axiom</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.red, fontFamily: 'Georgia, serif', marginBottom: 4 }}>{corpus.withoutAxiom.outcome}</div>
            <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>{corpus.withoutAxiom.detail}</div>
          </div>
          <div style={{ background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.greenLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>With Axiom · Flagged 23 days early</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.green, fontFamily: 'Georgia, serif', marginBottom: 4 }}>{corpus.withAxiom.outcome}</div>
            <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>{corpus.withAxiom.detail}</div>
          </div>
        </div>

        {/* Corpus CTA */}
        <div onClick={() => onOpenCorpus('dal-01')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 8, padding: '12px 16px', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(154,123,46,0.10)'}
          onMouseLeave={e => e.currentTarget.style.background = T.goldBg}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(154,123,46,0.10)', border: `1px solid ${T.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 10, color: T.gold, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 2 }}>Corpus Match · 98% confidence</div>
              <div style={{ fontSize: 13, color: T.textPrimary, fontWeight: 600 }}>eBay Salt Lake City 2018 · Identical CRAC/electrical conflict · 19-day recovery documented</div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>From $5B institutional knowledge corpus · 47 commissioning sequences analyzed</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: T.gold, flexShrink: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700 }}>View reasoning</span>
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke={T.gold} strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SIGNALS RAIL — light
// ════════════════════════════════════════════════════════════════════════════
const SignalsRail = ({ onOpenCorpus }) => {
  const signals = [
    { sev: 'critical', label: 'DALLAS TX',  text: 'IST sequence conflict · 23-day slip',  value: '$46M at risk',           id: 'dal-01', policy: 'COMM-001' },
    { sev: 'warn',     label: 'PHOENIX AZ', text: 'APS grid queue +6mo above median',      value: 'Advance pre-application', id: 'phx-02', policy: 'SITE-001' },
    { sev: 'warn',     label: '3 SITES',    text: 'Vertiv UPS +11wk · shared SKU',         value: 'Parallel Eaton proc.',    id: 'atl-04', policy: 'BILD-001' },
    { sev: 'ok',       label: 'ASHBURN VA', text: 'Exit window Q2 2026 · optimal',         value: '$2.8B implied value',    id: 'iad-03', policy: 'CPTL-001' },
  ];
  const sevStyle = {
    critical: { lineColor: T.redLight,   dotColor: T.redLight,   bg: T.redBg,   border: T.redBorder,   text: T.red   },
    warn:     { lineColor: T.amberLight, dotColor: T.amberLight, bg: T.amberBg, border: T.amberBorder, text: T.amber },
    ok:       { lineColor: T.greenLight, dotColor: T.greenLight, bg: T.greenBg, border: T.greenBorder, text: T.green },
  };
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '16px', marginBottom: 20, boxShadow: '0 1px 4px rgba(26,24,20,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.gold }} />
        <span style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.2em' }}>AXIOM — Proactive Signals</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {signals.map((s, i) => {
          const st = sevStyle[s.sev];
          return (
            <div key={i} onClick={() => onOpenCorpus(s.id)}
              style={{ background: st.bg, border: `1px solid ${st.border}`, borderRadius: 6, padding: '10px 12px', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <div style={{ height: 2, background: st.lineColor, borderRadius: 1, marginBottom: 8 }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: st.dotColor, animation: s.sev === 'critical' ? 'pulse 1.5s infinite' : 'none' }} />
                  <span style={{ fontSize: 9, fontWeight: 800, color: st.text, letterSpacing: '0.1em' }}>{s.label}</span>
                </div>
                <PolicyPill id={s.policy} fired={s.sev !== 'ok'} />
              </div>
              <div style={{ fontSize: 11, color: T.textPrimary, lineHeight: 1.4, marginBottom: 4, fontWeight: 500 }}>{s.text}</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>{s.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROJECT CARD — light
// ════════════════════════════════════════════════════════════════════════════
const ProjectCard = ({ project, onClick, isSelected }) => {
  const sc = scoreStyle(project.phaseScore, project.phase);
  const pp = PROJECT_POLICIES[project.id] || { assigned: [], triggered: [] };
  const sevStyle = {
    critical: { dot: T.redLight, bg: T.redBg, border: T.redBorder, text: T.red },
    warn:     { dot: T.amberLight, bg: T.amberBg, border: T.amberBorder, text: T.amber },
    ok:       { dot: T.greenLight, bg: T.greenBg, border: T.greenBorder, text: T.green },
  }[project.signal.sev];

  const circumference = 2 * Math.PI * 18;
  const scoreColor = project.phaseScore < 35 ? T.redLight : project.phaseScore < 55 ? '#B05010' : project.phaseScore < 80 ? T.amberLight : project.phase === 'Exit' ? T.navy : T.greenLight;

  return (
    <div onClick={() => onClick(project)}
      style={{
        background: T.bgCard, border: `1px solid ${isSelected ? T.goldBorder : T.border}`,
        borderRadius: 8, padding: 16, cursor: 'pointer', transition: 'all 0.15s',
        boxShadow: isSelected ? `0 0 0 2px ${T.goldBorder}` : '0 1px 4px rgba(26,24,20,0.05)',
        position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderStrong; e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,24,20,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = isSelected ? T.goldBorder : T.border; e.currentTarget.style.boxShadow = isSelected ? `0 0 0 2px ${T.goldBorder}` : '0 1px 4px rgba(26,24,20,0.05)'; }}
    >
      {/* Gold top rule for triggered */}
      {pp.triggered.length > 0 && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: T.goldLight, borderRadius: '8px 8px 0 0' }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Georgia, serif' }}>{project.name}</div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.location} · {project.type}</div>
        </div>
        <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
          <svg width="44" height="44" style={{ transform: 'rotate(-90deg)' }} viewBox="0 0 44 44">
            <circle cx="22" cy="22" r="18" fill="none" stroke={T.border} strokeWidth="3"/>
            <circle cx="22" cy="22" r="18" fill="none" stroke={scoreColor} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={circumference * (1 - (project.phaseScore || 0) / 100)}/>
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: scoreColor, fontFamily: 'Georgia, serif' }}>{project.phaseScore}</span>
          </div>
        </div>
      </div>

      {/* Phase track */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
        {PHASES.map(ph => {
          const phData = project.phases.find(p => p.name === ph);
          const isCurrent = ph === project.phase;
          const isDone = phData?.done && !isCurrent;
          return (
            <div key={ph} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{ height: 2, width: '100%', borderRadius: 1, background: isCurrent ? phaseColor(ph) : isDone ? T.borderStrong : T.border }} />
              {isCurrent && <div style={{ fontSize: 7, fontWeight: 700, color: phaseColor(ph), letterSpacing: '0.05em' }}>{ph.slice(0,3).toUpperCase()}</div>}
            </div>
          );
        })}
      </div>

      {/* Signal */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: sevStyle.bg, border: `1px solid ${sevStyle.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: sevStyle.dot, flexShrink: 0, marginTop: 3, animation: project.signal.sev === 'critical' ? 'pulse 1.5s infinite' : 'none' }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: sevStyle.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.signal.text}</div>
          <div style={{ marginTop: 3 }}><PolicyPill id={project.signal.policy} fired={project.signal.sev !== 'ok'} /></div>
        </div>
      </div>

      {/* Policy row */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
        {pp.triggered.map(id => <PolicyPill key={id} id={id} fired={true} />)}
        {pp.assigned.filter(id => !pp.triggered.includes(id)).slice(0, 2).map(id => <PolicyPill key={id} id={id} fired={false} />)}
        {pp.assigned.length > pp.triggered.length + 2 && (
          <span style={{ fontSize: 9, color: T.textMuted }}>+{pp.assigned.length - pp.triggered.length - 2}</span>
        )}
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
        {project.metrics.map((m, i) => (
          <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: '6px 8px' }}>
            <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: m.warn ? T.amberLight : T.textPrimary, marginTop: 2, fontFamily: 'Georgia, serif' }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Capital bar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 9, color: T.textMuted }}>Capital deployed</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.textSecondary, fontFamily: 'Georgia, serif' }}>{project.capital}</span>
        </div>
        <div style={{ height: 3, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: scoreColor, width: `${project.capitalPct}%`, borderRadius: 2 }} />
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 12, right: 14, fontSize: 14, color: T.textMuted }}>›</div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// DRILL PANEL — light
// ════════════════════════════════════════════════════════════════════════════
const DrillPanel = ({ project, onClose, onOpenCorpus }) => {
  const sc = scoreStyle(project.phaseScore, project.phase);
  const corpus = CORPUS[project.id];
  const pp = PROJECT_POLICIES[project.id] || { assigned: [], triggered: [] };
  const scoreColor = project.phaseScore < 35 ? T.redLight : project.phaseScore < 55 ? '#B05010' : project.phaseScore < 80 ? T.amberLight : project.phase === 'Exit' ? T.navy : T.greenLight;
  const circumference = 2 * Math.PI * 18;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,24,20,0.3)' }} onClick={onClose} />
      <div style={{
        width: '100%', maxWidth: 420, background: T.bgCard, borderLeft: `1px solid ${T.border}`,
        height: '100%', overflowY: 'auto', position: 'relative', zIndex: 1,
        animation: 'slideInRight 0.22s ease-out', boxShadow: '-8px 0 32px rgba(26,24,20,0.10)',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ height: 3, background: T.goldLight }} />

        <div style={{ padding: '20px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 4 }}>Axiom Build · {project.phase}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{project.name}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{project.location} · {project.type}</div>
            </div>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.textSecondary, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>

          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
            {pp.triggered.map(id => <PolicyPill key={id} id={id} fired={true} />)}
            {pp.assigned.filter(id => !pp.triggered.includes(id)).map(id => <PolicyPill key={id} id={id} fired={false} />)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
              <svg width="44" height="44" style={{ transform: 'rotate(-90deg)' }} viewBox="0 0 44 44">
                <circle cx="22" cy="22" r="18" fill="none" stroke={T.border} strokeWidth="3"/>
                <circle cx="22" cy="22" r="18" fill="none" stroke={scoreColor} strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={circumference * (1 - project.phaseScore / 100)}/>
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: scoreColor, fontFamily: 'Georgia, serif' }}>{project.phaseScore}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor }}>{sc.label} · {project.phase}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{project.capital} deployed · {project.tenant}</div>
            </div>
          </div>
        </div>

        {/* Lifecycle */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Development Lifecycle</div>
          {project.phases.map(ph => {
            const isCurrent = ph.name === project.phase;
            const phC = phaseColor(ph.name);
            const psc = scoreStyle(ph.score, ph.name);
            return (
              <div key={ph.name} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, marginBottom: 3,
                background: isCurrent ? `rgba(26,24,20,0.04)` : 'transparent',
                border: `1px solid ${isCurrent ? T.border : 'transparent'}`,
                opacity: !isCurrent && !ph.done ? 0.35 : 1,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: isCurrent ? phC : ph.done ? T.borderStrong : T.border, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: isCurrent ? 700 : 400, color: isCurrent ? T.textPrimary : T.textSecondary, flex: 1 }}>{ph.name}</span>
                {ph.score && <span style={{ fontSize: 11, fontWeight: 700, color: psc.color, fontFamily: 'Georgia, serif' }}>{ph.score}</span>}
                {ph.done && !isCurrent && <span style={{ fontSize: 10, color: T.textMuted }}>✓</span>}
                <span style={{ fontSize: 10, color: T.textMuted, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ph.note}</span>
              </div>
            );
          })}
        </div>

        {/* Metrics */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Key Metrics</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {project.allMetrics.map((m, i) => (
              <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{m.label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: m.warn ? T.amberLight : T.textPrimary, marginTop: 2, fontFamily: 'Georgia, serif' }}>{m.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Corpus CTA */}
        <div style={{ padding: '16px 20px' }}>
          <div onClick={() => onOpenCorpus(project.id)}
            style={{ background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 8, padding: '14px 16px', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(154,123,46,0.10)'}
            onMouseLeave={e => e.currentTarget.style.background = T.goldBg}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.gold }} />
                <span style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Axiom Intelligence</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: T.gold }}>View corpus</span>
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={T.gold} strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </div>
            </div>
            <p style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.6, margin: '0 0 10px' }}>{project.insight}</p>
            {corpus && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: `1px solid ${T.goldBorder}` }}>
                <span style={{ fontSize: 10, color: T.textMuted }}>Value protected:</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: T.greenLight, fontFamily: 'Georgia, serif' }}>{corpus.savedValue}</span>
                <span style={{ marginLeft: 'auto', fontSize: 9, color: T.textMuted }}>{corpus.corpusMatches.length} matches · {corpus.confidence}%</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════════════════
const AxiomPortfolioV3 = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [corpusProjectId, setCorpusProjectId] = useState(null);
  const handleCardClick = useCallback((project) => {
    setSelectedProject(prev => prev?.id === project.id ? null : project);
  }, []);

  const stats = [
    { label: 'Portfolio Capital', value: '$4.2B',   color: T.textPrimary },
    { label: 'Active Projects',   value: '6',        color: T.textPrimary },
    { label: 'Total Capacity',    value: '1,450 MW', color: T.textPrimary },
    { label: 'Capital at Risk',   value: '$74M',     color: T.redLight },
    { label: 'Value Protected',   value: '$280M',    color: T.greenLight },
    { label: 'Policies Active',   value: '12',       color: T.gold },
    { label: 'Triggered',         value: '5',        color: T.amberLight },
  ];

  return (
    <div style={{ minHeight: '100vh', width: '100%', background: T.bg, fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif", color: T.textPrimary, position: 'relative' }}>

      {/* Header */}
      <div style={{ background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '0 24px', position: 'relative', boxShadow: '0 1px 4px rgba(26,24,20,0.06)' }}>
        {/* Gold rule at very top */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.goldLight} 0%, ${T.gold} 30%, transparent 70%)` }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, padding: '22px 0 18px' }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.25em', marginBottom: 6 }}>Axiom Intelligence Platform</div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 36, height: 36, borderRadius: 6, background: T.goldBg, border: `1px solid ${T.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                </svg>
              </span>
              AI Factory Portfolio
            </h1>
            <p style={{ fontSize: 12, color: T.textSecondary, margin: '5px 0 0', letterSpacing: '0.01em' }}>
              $5B institutional knowledge · Cradle-to-exit · Policy-governed intelligence
            </p>
          </div>

          {/* Stats row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {stats.map((s, i) => (
              <div key={s.label} style={{ textAlign: 'right', paddingLeft: i > 0 ? 24 : 0, borderLeft: i > 0 ? `1px solid ${T.border}` : 'none' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: 'Georgia, serif' }}>{s.value}</div>
                <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px 24px' }}>
        <HeroCard onOpenCorpus={setCorpusProjectId} />
        <SignalsRail onOpenCorpus={setCorpusProjectId} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, paddingBottom: 32 }}>
          {PROJECTS.map(project => (
            <ProjectCard key={project.id} project={project} onClick={handleCardClick} isSelected={selectedProject?.id === project.id} />
          ))}
        </div>
      </div>

      {selectedProject && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setSelectedProject(null)} />
          <DrillPanel project={selectedProject} onClose={() => setSelectedProject(null)} onOpenCorpus={setCorpusProjectId} />
        </>
      )}

      {corpusProjectId && <CorpusModal projectId={corpusProjectId} onClose={() => setCorpusProjectId(null)} />}

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeScaleIn  { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes pulse        { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      `}</style>
    </div>
  );
};

export default AxiomPortfolioV3;
